import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import {
  chatAction,
  formatWhen,
  initials,
  timeLeft,
} from "../../../../utils/automation-activity";
import { StatusChip } from "./ActivityLog";
import { valueNames } from "./FilterChips";

const DOT_BY_STATE = {
  done: "done",
  matched: "done",
  current: "current",
  not_matched: "bad",
  skipped: "bad",
  exited: "bad",
  upcoming: "later",
  not_reached: "later",
};

const closePanel = () =>
  submit("/actions/refresh?", { method: "GET", body: { person: "" } });

const useJourneyText = () => {
  const { t, i18n } = useTranslation("coaching-portal");
  const tr = (key, params) => t(`emailAutomations.activity.${key}`, params);
  const when = (value) =>
    formatWhen(
      value,
      { today: tr("today"), yesterday: tr("yesterday") },
      { locale: i18n.language }
    );
  const unitText = (count, unit) => tr(`units.${unit}`, { count });
  return { t, tr, when, unitText };
};

const filterNames = (filters, options, t) =>
  Object.entries(filters ?? {})
    .map(([field, values]) => valueNames(field, values, options, t).join(", "))
    .join(" · ");

/** One step of the journey: its title, and a line under it that depends on how far it got. */
const entryText = (
  entry,
  { tr, t, unitText, when },
  { triggers, options, enteredAt }
) => {
  switch (entry.kind) {
    case "trigger": {
      const name = triggers.find((item) => item.key === entry.trigger)?.name;
      return {
        title: tr("journey.trigger", { name: name ?? entry.trigger }),
        note: when(entry.at),
      };
    }
    case "filter": {
      const names = filterNames(entry.filters, options, t);
      if (entry.state === "not_matched") {
        return {
          title: tr("journey.filterNoMatch", { names }),
          note: when(entry.at),
        };
      }
      return {
        title: tr(
          entry.state === "matched"
            ? "journey.filterMatched"
            : "journey.filter",
          { names }
        ),
        note:
          entry.state === "matched"
            ? when(enteredAt)
            : tr(`journey.${entry.state}`),
      };
    }
    case "email": {
      const title = tr("journey.email", {
        subject: entry.subject || tr("noSubject"),
      });
      if (entry.state === "done") {
        const sent = tr("journey.sent", {
          when: when(entry.sentAt ?? entry.at),
        });
        return {
          title,
          note: entry.openedAt ? `${sent} · ${tr("journey.opened")}` : sent,
        };
      }
      if (entry.state === "skipped") {
        return {
          title,
          note: tr("journey.skipped", {
            reason: tr(`skipReasons.${entry.skipReason}`, {
              defaultValue: entry.skipReason ?? "",
            }),
          }),
        };
      }
      return { title, note: tr(`journey.${entry.state}`) };
    }
    case "delay": {
      const title = tr("journey.delay", {
        amount: entry.amount,
        unit: unitText(entry.amount, entry.unit),
      });
      return {
        title,
        note:
          entry.state === "current"
            ? tr("journey.nextStep", { when: when(entry.nextRunAt) })
            : tr(`journey.${entry.state}`),
      };
    }
    default:
      return {
        title: tr("journey.exited", {
          reason: tr(`exitReasons.${entry.reason}`, {
            defaultValue: tr(
              `exitEvents.${String(entry.reason).replace("exit_rule:", "")}`,
              {
                defaultValue: entry.reason ?? "",
              }
            ),
          }),
        }),
        note: when(entry.at),
      };
  }
};

/** The side panel of a person: where they are in the automation and everything that happened. */
const JourneyPanel = ({
  automationId,
  enrollmentId,
  journey,
  triggers,
  options,
}) => {
  const text = useJourneyText();
  const { tr, when } = text;
  const { person } = journey;
  const left = journey.nextRunAt ? timeLeft(journey.nextRunAt) : null;
  const row = { status: journey.status, step: null, skipReason: null };
  return (
    <aside className="al-drawer" aria-label={person.name || person.email}>
      <header>
        <span className="al-avatar al-avatar--lg" aria-hidden="true">
          {initials(person)}
        </span>
        <div>
          <h3>{person.name || person.email}</h3>
          <p>{[person.email, journey.plan].filter(Boolean).join(" · ")}</p>
        </div>
        <button type="button" onClick={closePanel} aria-label={tr("close")}>
          <img src="/img/new-icons/close-modal.svg" alt="" />
        </button>
      </header>
      <div className="al-drawer__state">
        <StatusChip row={row} />
        {left && journey.status === "waiting" ? (
          <span className="al-chip al-chip--waiting">
            {tr("status.waitingLeft", {
              amount: left.count,
              unit: text.unitText(left.count, left.unit),
            })}
          </span>
        ) : null}
        <span>{tr("entered", { when: when(journey.enteredAt) })}</span>
      </div>
      <h4>{tr("journey.title")}</h4>
      <ol className="al-journey">
        {journey.journey.map((entry) => {
          const { title, note } = entryText(entry, text, {
            triggers,
            options,
            enteredAt: journey.enteredAt,
          });
          return (
            <li
              key={`${entry.kind}-${entry.position ?? "x"}`}
              data-dot={DOT_BY_STATE[entry.state] ?? "later"}
              data-state={entry.state}
            >
              <strong>{title}</strong>
              <small>{note}</small>
            </li>
          );
        })}
      </ol>
      <footer>
        {person.userId ? (
          <button
            type="button"
            data-button-type="secondary"
            onClick={() =>
              submit(chatAction(person.userId), { method: "POST", body: {} })
            }
          >
            {tr("actions.messageButton")}
          </button>
        ) : null}
        <button
          type="button"
          data-button-type="danger"
          disabled={!journey.inside}
          onClick={() =>
            submit(
              `/actions/automations/${automationId}/activity/${enrollmentId}/remove`,
              {
                method: "POST",
                body: {},
              }
            )
          }
        >
          {tr("actions.removeButton")}
        </button>
      </footer>
    </aside>
  );
};

JourneyPanel.propTypes = {
  automationId: PropTypes.string.isRequired,
  enrollmentId: PropTypes.string.isRequired,
  journey: PropTypes.shape({
    person: PropTypes.object.isRequired,
    status: PropTypes.string.isRequired,
    inside: PropTypes.bool,
    plan: PropTypes.string,
    enteredAt: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.instanceOf(Date),
    ]),
    nextRunAt: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.instanceOf(Date),
    ]),
    journey: PropTypes.array.isRequired,
  }).isRequired,
  triggers: PropTypes.array.isRequired,
  options: PropTypes.object,
};

export default JourneyPanel;
